import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runAlertScanner } from "@/lib/admin.functions";
import { listNotificationsAdmin } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertTriangle, Clock, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/admin/alerts")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: AlertsAdmin,
});

function AlertsAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const runAlertFn = useServerFn(runAlertScanner);
  const listNotifFn = useServerFn(listNotificationsAdmin);
  const [busy, setBusy] = useState<string | null>(null);
  const [lateHours, setLateHours] = useState("48");
  const [expiryDays, setExpiryDays] = useState("30");
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const sort = urlSearch.sort ?? "desc";
  // Default last 7d
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: urlSearch.end }),
    [effectiveStart, urlSearch.end],
  );

  const { data: notifications = [], refetch } = useQuery({
    queryKey: ["all-notif", tenantId, fromIso, toIso, sort],
    enabled: !!tenantId,
    queryFn: () => listNotifFn({ data: { tenantId: tenantId!, fromIso, toIso, sort } }),
  });

  const run = async (scanner: "scan_low_stock" | "scan_late_shipments" | "scan_expiring_lots") => {
    if (!tenantId) return;
    setBusy(scanner);
    try {
      const res = await runServerFn(
        () => runAlertFn({ data: {
          tenantId,
          scanner,
          hours: scanner === "scan_late_shipments" ? Number(lateHours) || 48 : undefined,
          days: scanner === "scan_expiring_lots" ? Number(expiryDays) || 30 : undefined,
        } }),
        "Run scanner",
      );
      toast.success(`Created ${res.created} new ${scanner.replace("scan_", "").replace("_", " ")} alert(s)`);
      qc.invalidateQueries({ queryKey: ["all-notif", tenantId] });
      qc.invalidateQueries({ queryKey: ["notifications", tenantId] });
    } catch {
      // toast handled
    } finally {
      setBusy(null);
    }
  };

  const sevColor = (s: string) =>
    s === "critical" ? "destructive" : s === "warning" ? "secondary" : "outline";

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Alerts & Scanners</h2>
        <p className="text-sm text-muted-foreground">Run health scans manually; results appear in the bell and below.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Bell className="h-4 w-4" />Low stock</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            <p className="text-xs text-muted-foreground">SKUs at or below reorder point.</p>
            <Button size="sm" onClick={() => run("scan_low_stock")} disabled={busy !== null}>
              {busy === "scan_low_stock" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Run scan
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" />Late shipments</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Label className="text-xs">Open longer than (hours)</Label>
            <Input type="number" value={lateHours} onChange={(e) => setLateHours(e.target.value)} className="h-8" />
            <Button size="sm" onClick={() => run("scan_late_shipments")} disabled={busy !== null}>
              {busy === "scan_late_shipments" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Run scan
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Expiring lots</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Label className="text-xs">Within next (days)</Label>
            <Input type="number" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} className="h-8" />
            <Button size="sm" onClick={() => run("scan_expiring_lots")} disabled={busy !== null}>
              {busy === "scan_expiring_lots" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Run scan
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Recent Notifications</h3>
        <div className="flex gap-2 items-center flex-wrap">
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => update({ start: next.start, end: next.end })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
          />
          <Button size="sm" variant="outline" onClick={() => refetch()}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </div>
      </div>
      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Body</TableHead>
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {notifications.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No notifications yet — run a scan above.</TableCell></TableRow>
            ) : notifications.map((n) => (
              <TableRow key={n.id}>
                <TableCell className="text-xs whitespace-nowrap">{format(new Date(n.created_at), "MMM d HH:mm")}</TableCell>
                <TableCell className="text-xs">{n.kind}</TableCell>
                <TableCell><Badge variant={sevColor(n.severity) as never}>{n.severity}</Badge></TableCell>
                <TableCell className="text-sm">{n.title}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md truncate">{n.body}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{n.read_at ? "read" : "unread"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
